// DatadogRumSectionBoundary.tsx
import { ErrorBoundary } from "@datadog/browser-rum-react";
import { Outlet, useLocation } from "react-router-dom";

import { useEffect, useState } from "react";

import { useAuthStore } from "@/stores/authStore";

import { normalizeViewName } from "./rum-privacy";
import { rumService } from "./rum-service";
import { getRumConfig } from "./rum.config";

// Wraps every route and initializes RUM. All Datadog calls live in rumService.
export function DatadogRumSectionBoundary(): JSX.Element {
  const location = useLocation();
  // Login sets these through updateState() in several flows, and another tab's
  // login/logout arrives through the auth-storage listener, so follow the store.
  const { isAuthenticated, customer } = useAuthStore();
  const [rumInitializedState, setRumInitializedState] = useState(false);

  useEffect(() => {
    if (!rumService.isEnabled()) {
      console.warn(`[RUM] RUM is not enabled for the current environment: ${process.env.APP_ENV}`);
      return;
    }
    rumService.start(getRumConfig());
    rumService.enterApp();
    setRumInitializedState(true);
  }, []);

  useEffect(() => {
    if (rumInitializedState) {
      rumService.restoreUser({ isAuthenticated, customer });
    }
  }, [rumInitializedState, isAuthenticated, customer]);

  useEffect(() => {
    const viewName = rumInitializedState && normalizeViewName(location.pathname);
    if (viewName) {
      rumService.startView(viewName);
    }
  }, [rumInitializedState, location.pathname]);

  return (
    <ErrorBoundary
      fallback={({ resetError }) => (
        <div>
          <p>Something went wrong.</p>
          <button onClick={resetError}>Try again</button>
        </div>
      )}
    >
      <Outlet />
    </ErrorBoundary>
  );
}
